import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getJobTemplates, jobTemplatesDelete } from "../../redux/Thunk/AdminModule/AdminThunk";
import { Typography, Box, Card, CardContent, Grid, IconButton } from "@mui/material";
import { FilterAlt as FilterAltIcon } from "@mui/icons-material";
import GenericTable from "../../Components/Ui_elements/GenericTable";
import Btn from "../../Components/Ui_elements/Btn";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PopupModal from "../../Components/Ui_elements/PopupModal";
import TextFieldComponent from "../../Components/TextFieldComponent";
import DropdownComponent from "../../Components/DropdownComponent";
import {
  getGeneralMasterDropDownList,
  updateJobTemplate,
} from "../../redux/Thunk/DashboardModule/DashboardThunk";
import EditJobTemplateModal from "./EditJobTemplateModal";
import { showSnackbar } from "../../redux/Slice/snackbar/snackbarSlice";
import { setjobTemplateList } from "../../redux/Slice/AdminModule/AdminSlice";

const initialFormState = {
  template_name: "",
  client_id: "",
  indirect_client_id: "",
  job_type_id: "",
  source_language: "",
  target_language: "",
};

const JobTemplates = () => {
  // Redux state
  const { jobTemplateList, loading } = useSelector(state => state.root.AdminModule);
  const { dropdowns } = useSelector(state => state.root.DashboardModule);

  // Local state
  const dispatch = useDispatch();
  const [openForDelete, setOpenForDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState({ column: 1, dir: "asc" });
  const [formData, setFormData] = useState(initialFormState);
  const [editJobTemp, setEditJobTemp] = useState(null);
  const [errors, setErrors] = useState({});

  // Memoized dropdown options
  const dropdownOptions = {
    clientOptions:
      dropdowns?.clientList?.map(client => ({
        label: client.vUserName || "",
        value: String(client.iClientId || ""),
      })) || [],

    indDirectClientOptions:
      dropdowns?.indirectClientList?.map(item => ({
        label: item.vUserName || "",
        value: String(item.iClientId || ""),
      })) || [],

    jobTypeOptions:
      dropdowns?.jobTypeList?.map(type => ({
        label: type.service_name || "",
        value: String(type.job_id || ""),
      })) || [],

    languageOptions:
      dropdowns?.languages?.map(language => ({
        label: language.title || "",
        value: String(language.title || ""),
      })) || [],
  };
  const handleSave = request => {
    dispatch(updateJobTemplate(request)).then(() => {
      fetchData(page, pageSize, order, formData);
      setEditJobTemp(null);
    });
  };
  // Fetch data function
  const fetchData = (page, pageSize, order, filterParams = formData) => {
    const params = {
      page: page + 1,
      perPage: pageSize,
      order: order.column ? [order] : [],
      search: "",
      filterParams,
    };
    dispatch(getJobTemplates(params));
  };

  useEffect(() => {
    dispatch(
      getGeneralMasterDropDownList({
        clientList: true,
        indirectClient: true,
        languages: true,
        jobType: true,
        resource: true,
      })
    );
  }, []);

  // Initial data fetch

  useEffect(() => {
    fetchData(page, pageSize, order);
  }, [page, pageSize, order]);

  // Table change handler
  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        break;
      case "changeRowsPerPage":
        setPageSize(tableState.rowsPerPage);
        setPage(0);
        break;
      case "sort":
        setOrder({
          column: tableState.activeColumn,
          dir: tableState.sortOrder.direction,
        });
        break;
      default:
        break;
    }
  };

  // Form handlers
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    const isFormEmpty = Object.values(formData).every(value => value === "");

    if (isFormEmpty) {
      dispatch(
        showSnackbar({
          message: "Please fill in at least one field.",
          type: "info",
        })
      );
      return; // Exit the function early if form is empty
    }
    setPage(0);
    fetchData(page, pageSize, order);
  };

  const handleReset = () => {
    const resetFilters = initialFormState;

    setFormData(resetFilters);
    setPage(0);

    // Reload data WITHOUT filters
    fetchData(0, pageSize, order, resetFilters);
  };


  // Table configuration
  const columns = [
    {
      name: "rowNumber",
      label: "#",
      options: {
        sort: false,
        customBodyRender: (_, tableMeta) => page * pageSize + tableMeta.rowIndex + 1,
      },
    },
    { name: "template_name", label: "Template Name", options: { sort: true } },
    { name: "job_type_name", label: "Job Type", options: { sort: true } },
    { name: "clientName", label: "Direct Client", options: { sort: true } },
    { name: "accountName", label: "Indirect Client", options: { sort: true } },
    { name: "sourceLang", label: "Source Language", options: { sort: true } },
    { name: "targetLang", label: "Target Language", options: { sort: true } },
    {
      name: "actions",
      label: "Actions",
      options: {
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const rowData = jobTemplateList?.data?.[tableMeta.rowIndex] || {};
          return (
            <Box display="flex" gap={1}>
              <IconButton
                size="small"
                color="primary"
                title="Edit"
                onClick={() => setEditJobTemp(rowData)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                title="Delete"
                onClick={() => {
                  setOpenForDelete(true);
                  setDeleteId(rowData.id);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    },
  ];

  const data = jobTemplateList?.data?.map(item => ({ ...item, rowNumber: 0 })) || [];

  const handleConfirmDelete = () => {
    dispatch(jobTemplatesDelete(deleteId)).then(() => {
      fetchData(page, pageSize, order);
      setOpenForDelete(false);
    });
  };

  return (
    <>
      <Box>
        <Typography variant="h5" sx={{ mb: 3 }}>
          Job Templates
        </Typography>

        {/* Filter Form */}

        <Box sx={{ mb: 3, backgroundColor: "#fff" }}>
          <form onSubmit={handleSubmit}>
            <CardContent>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextFieldComponent
                    name="template_name"
                    label="Template Name"
                    value={formData.template_name}
                    onChange={handleInputChange}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <DropdownComponent
                    label="Direct Client"
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    options={dropdownOptions.clientOptions}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <DropdownComponent
                    label="Indirect Client"
                    name="indirect_client_id"
                    value={formData.indirect_client_id}
                    onChange={handleInputChange}
                    options={dropdownOptions.indDirectClientOptions}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <DropdownComponent
                    label="Job Type"
                    name="job_type_id"
                    value={formData.job_type_id}
                    onChange={handleInputChange}
                    options={dropdownOptions.jobTypeOptions}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <DropdownComponent
                    label="Source Language"
                    name="source_language"
                    value={formData.source_language}
                    onChange={handleInputChange}
                    options={dropdownOptions.languageOptions}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <DropdownComponent
                    label="Target Language"
                    name="target_language"
                    value={formData.target_language}
                    onChange={handleInputChange}
                    options={dropdownOptions.languageOptions}
                    size="small" // Added small size
                    fullWidth
                    isNotMandatory={true}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}> {/* Reduced gap and margin */}
                    <Btn 
                      variant="contained" 
                      startIcon={<FilterAltIcon />} 
                      type="submit"
                      size="small" // Added small size to button
                    >
                      Filter
                    </Btn>
                    <Btn 
                      variant="outlined" 
                      onClick={handleReset} 
                      type="button"
                      size="small" // Added small size to button
                    >
                      Reset
                    </Btn>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </form>
        </Box>

        {/* Data Table */}
        <Box className="whiteBackground" sx={{ p: 1, backgroundColor: "#fff" }}>
          <CardContent sx={{ p: "0px !important" }}>
            <GenericTable
              columns={columns}
              data={data}
              showAction={false}
              options={{
                selectableRows: "none",
                serverSide: true,
                count: jobTemplateList?.recordsTotal || 0,
                page: page,
                rowsPerPage: pageSize,
                rowsPerPageOptions: [10, 25, 50, 100],
                search: false,
                download: false,
                print: false,
                filter: false,
                viewColumns: false,
                sortOrder: {
                  name: columns[order?.column]?.name || "",
                  direction: order.dir,
                },
                onTableChange: handleTableChange,
              }}
            />
          </CardContent>
        </Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <PopupModal
        maxWidth="sm"
        size={"sm"}
        fullWidth
        open={openForDelete}
        handleClose={() => setOpenForDelete(false)}
        actions={
          <Grid container columnGap={2} justifyContent="end">
            <Btn onClick={handleConfirmDelete}>Yes</Btn>
            <Btn onClick={() => setOpenForDelete(false)}>Cancel</Btn>
          </Grid>
        }
        title="Delete Template"
      >
        <Typography>Are you sure you want to delete this template?</Typography>
      </PopupModal>

      <EditJobTemplateModal
        open={editJobTemp}
        onClose={() => setEditJobTemp(null)}
        onSave={handleSave}
      />
    </>
  );
};

export default JobTemplates;